/* RitFit BCRF badge. Only loaded by the dedicated BCRF section. */
(() => {
  'use strict';
  const normalize = value => String(value || '').normalize('NFKC').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
  const hasTerm = (value, terms) => terms.some(term => {
    const token = normalize(term);
    return token && (` ${normalize(value)} `).includes(` ${token} `);
  });
  function eligible(config, variant) {
    if (config.mode === 'off' || !variant) return false;
    if (config.mode === 'all') return true;
    if (config.mode !== 'pink') return false;
    if ((config.pinkVariantIds || []).some(id => String(id).trim() === String(variant.id))) return true;
    // Campaign policy: any pink item in the selected combination qualifies.
    // The legacy primary-color mode remains available for the original test.
    if (config.matchScope === 'any_option') {
      return Array.isArray(variant.options) && variant.options.some(value => hasTerm(value, config.pinkTerms));
    }
    const colorIndex = config.optionNames.findIndex(name => config.colorNames.some(term => normalize(name) === normalize(term)));
    return colorIndex >= 0 && hasTerm(variant.options[colorIndex], config.pinkTerms);
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = { eligible, hasTerm };
  if (typeof window === 'undefined' || customElements.get('rfbcrf-controller')) return;

  class BcrfController extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      try {
        this.config = JSON.parse(this.querySelector('[data-rfbcrf-config]').textContent);
      } catch (_) { return; }
      this.variants = new Map(this.config.variants.map(variant => [String(variant.id), variant]));
      const signal = this.abort.signal;
      document.addEventListener('variant:change', event => {
        if (event.target !== this.form || !event.detail) return;
        this.update(event.detail.variant || null);
      }, { signal });
      document.addEventListener('shopify:section:load', () => this.mount(), { signal });
      customElements.whenDefined('x-popover').then(() => {
        if (this.isConnected && !signal.aborted) this.mount();
      });
    }

    mount() {
      const main = document.querySelector('.shopify-section--main-product');
      const info = main?.querySelector('.product-info');
      const title = info?.querySelector('.product-info__title');
      if (!main || !info || !title) return;
      if (this.main === main && this.view?.isConnected) return;
      const form = Array.from(main.querySelectorAll('form')).find(form =>
        (form.getAttribute('id') || '').includes(`product-form-${this.config.productId}-`) && form.elements.namedItem('id')
      );
      if (!form) return;
      this.unmount();
      this.main = main;
      this.form = form;
      this.mountAbort = new AbortController();
      const signal = this.mountAbort.signal;
      // Close before Impact disconnects its popover during an editor reload.
      main.addEventListener('shopify:section:unload', () => this.unmount(), { signal });
      // An unassigned preview template uses the actual product's normal form.
      const fragment = this.querySelector('[data-rfbcrf-markup]').content.cloneNode(true);
      this.view = fragment.querySelector('.rfbcrf');
      this.panel = this.view.querySelector('x-popover');
      this.trigger = this.view.querySelector('.rfbcrf-trigger');
      const setBusy = busy => {
        this.trigger.disabled = busy;
        this.panel.querySelector('[data-rfbcrf-close]').disabled = busy;
      };
      this.panel.addEventListener('toggle', () => setBusy(true), { signal });
      this.panel.addEventListener('dialog:after-show', () => setBusy(false), { signal });
      this.panel.addEventListener('dialog:after-hide', () => {
        setBusy(false);
        // Temporarily disabling a trigger during the transition can make the
        // theme's focus trap remember body instead of the trigger. Restore it
        // only when focus was not intentionally moved to another control.
        requestAnimationFrame(() => {
          if (!signal.aborted && this.isConnected && this.view && !this.view.hidden &&
              (document.activeElement === document.body || this.panel.contains(document.activeElement))) {
            this.trigger.focus({ preventScroll: true });
          }
        });
      }, { signal });
      this.panel.addEventListener('click', event => {
        if (event.target.closest('[data-rfbcrf-close]')) this.panel.hide();
      }, { signal });
      const badgeRow = Array.from(info.children).find(node =>
        node.classList.contains('product-info__badge-list') &&
        (node.compareDocumentPosition(title) & Node.DOCUMENT_POSITION_FOLLOWING)
      );
      if (badgeRow) {
        badgeRow.appendChild(fragment);
        this.ownRow = null;
      } else {
        this.ownRow = document.createElement('div');
        this.ownRow.className = 'rfbcrf-row';
        this.ownRow.appendChild(fragment);
        title.before(this.ownRow);
      }
      const selectedId = this.form.elements.namedItem('id').value;
      this.update(this.variants.get(String(selectedId)) || this.variants.get(String(this.config.selectedId)) || null);
    }

    update(variant) {
      if (!this.view) return;
      const show = eligible(this.config, variant);
      if (!show && this.panel.open) this.panel.hide();
      this.view.hidden = !show;
      if (this.ownRow) this.ownRow.hidden = !show;
      this.view.dataset.variantId = variant ? String(variant.id) : '';
    }

    unmount() {
      this.mountAbort?.abort();
      this.mountAbort = null;
      // A detached Impact dialog has already released its scroll lock.
      if (this.panel?.isConnected && this.panel.open) this.panel.hide();
      this.view?.remove();
      this.ownRow?.remove();
      // Impact may temporarily move its popover to body on mobile.
      if (this.panel?.isConnected) this.panel.remove();
      this.main = this.form = this.view = this.panel = this.trigger = this.ownRow = null;
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.abort = null;
      this.unmount();
    }
  }
  customElements.define('rfbcrf-controller', BcrfController);
})();
